// CSVImportDialog.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  Alert,
  Divider,
  Chip,
  Paper,
  TableContainer,
} from "@mui/material";
import {
  CloudDownload,
  Upload,
  ExpandMore,
  ExpandLess,
  Check,
  Warning,
  ErrorOutline,
  CheckCircleOutline,
  WarningAmber,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import axios from "axios";

const CSVImportDialog = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [importResults, setImportResults] = useState([]);
  const [showDetail, setShowDetail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isExit, setIsExit] = useState(false); // Track if the dialog is closed
  const fileInputRef = useRef();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  useEffect(() => {
    if (open) {
      
      setFile(null);
      setProgress(0);
      setImportResults([]);
      setShowDetail(true);
      setLoading(false);
    }
  }, [open]);
  // const handleDownloadTemplate = () => {
  //   const headersAndExamples = [
  //     ["username", "email", "phone", "password", "role"], // header
  //     ["john_doe", "john@example.com", "1234567890", "Password123!", "TENANT"],
  //     [
  //       "jane_land",
  //       "jane@landlord.com",
  //       "0987654321",
  //       "SecurePass456!",
  //       "LANDLORD",
  //     ],
  //     [
  //       "plumb_master",
  //       "plumb@example.com",
  //       "1122334455",
  //       "PipeFix789!",
  //       "SERVICE_PROVIDER",
  //     ],
  //   ];
  //   const ws = XLSX.utils.aoa_to_sheet(headersAndExamples);
  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "Template");
  //   XLSX.writeFile(wb, "user_template.csv");
  // };

  const handleUpload = async () => {
    if (!file) return;
    setIsExit(true); // Mark as exit to prevent further processing
    const reader = new FileReader();
    reader.onload = async (e) => {
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(worksheet);

      setLoading(true);

      setImportResults([]); // Clear previous
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/compound/createUser`,
            row
          );
          setImportResults((prev) => [
            ...prev,
            { row: i + 1, data: row, status: "success" },
          ]);
        } catch (error) {
          setImportResults((prev) => [
            ...prev,
            {
              row: i + 1,
              data: row,
              status: "failed",
              message: error?.response?.data?.error || "Unknown error",
            },
          ]);
        }
        setProgress(Math.round(((i + 1) / data.length) * 100));
      }
      setLoading(false);
      if (onImported) onImported();
    };
    setIsExit(false); // Reset exit state
    reader.readAsBinaryString(file);
  };

  const total = importResults.length;
  const success = importResults.filter((r) => r.status === "success").length;
  const failed = importResults.filter((r) => r.status === "failed").length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>CSV Import</DialogTitle>
      <DialogContent>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => fileInputRef.current?.click()}
            >
              Select CSV
            </Button>
            <input
              type="file"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>
          <Typography color="warning">{file?.name}</Typography>
        </Box>

        {loading && (
          <Box>
            <Typography mb={1}>Processing: {progress}%</Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {importResults.length > 0 && (
          <Box mt={2} sx={{ height: "65vh" }}>
            <Divider />
            {!loading && (
              <Box>
                <Box display="flex" gap={1} mt={3} mb={3}>
                  <Typography variant="h6">Result Summary:</Typography>
                  <Typography variant="h6" color="info">
                    Total -
                  </Typography>
                  <Typography variant="h6" color="info">
                    {total}
                  </Typography>
                  <Typography variant="h6" color="success">
                    Success -
                  </Typography>
                  <Typography variant="h6" color="success">
                    {success}
                  </Typography>
                  <Typography variant="h6" color="error">
                    Failed -
                  </Typography>
                  <Typography variant="h6" color="error">
                    {failed}
                  </Typography>
                </Box>

                {failed !== 0 && (
                  <Box display="flex" gap={2} mb={2}>
                    <WarningAmber color="warning" />
                    <Typography color="warning" mb={2}>
                      Can not create {failed} account. Please check the accuracy
                      of the CSV fileand try again.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Button
              startIcon={showDetail ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowDetail((prev) => !prev)}
            >
              {showDetail ? "Hide Detail" : "Show Detail"}
            </Button>
            <Divider />
            <Collapse in={showDetail}>
              <Paper
                sx={{
                  mt: 2,
                }}
              >
                <TableContainer
                  component={Box}
                  sx={{
                    maxHeight: "40vh",
                    overflow: "auto", // enable both horizontal and vertical
                    minWidth: "100%", // ensure it spans available space
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                      height: "8px", // 👈 ensures horizontal bar shows
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f0f0f0",
                      borderRadius: "8px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#c1c1c1",
                      borderRadius: "8px",
                      border: "2px solid #f0f0f0",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#a0a0a0",
                    },
                  }}
                >
                  <Table
                    size="small"
                    stickyHeader
                    sx={{ minWidth: 700, tableLayout: "auto" }}
                  >
                    <TableHead
                      sx={{
                        backgroundColor: "#0d1b2a",
                        "& th": {
                          backgroundColor: "#0d1b2a", // match your dark theme
                          color: "#fff",
                          top: 0,
                          position: "sticky", // ensures stickiness for custom headers too
                          zIndex: 1,
                        },
                      }}
                    >
                      <TableRow sx={{ backgroundColor: "#0d1b2a" }}>
                        <TableCell>Row</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResults.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.row}</TableCell>
                          <TableCell>{r.data.username}</TableCell>
                          <TableCell>{r.data.email}</TableCell>
                          <TableCell>{r.data.phone}</TableCell>
                          <TableCell>{r.data.role}</TableCell>
                          <TableCell>
                            <Chip
                              icon={
                                r.status === "success" ? (
                                  <CheckCircleOutline />
                                ) : (
                                  <ErrorOutline />
                                )
                              }
                              color={
                                r.status === "success" ? "success" : "error"
                              }
                              label={r.status}
                            ></Chip>
                          </TableCell>
                          <TableCell sx={{ minWidth: 300 }}>
                            <Typography
                              color="error"
                              sx={{
                                whiteSpace: "pre-wrap", // respects \n
                                wordBreak: "break-word", // breaks long words
                                fontSize: "0.85rem",
                              }}
                            >
                              {r.message || ""}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Collapse>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || loading}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CSVImportDialog;
